import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { Icon } from '../components/Icon';
import { useAuth, type AuthUser } from '../auth/AuthContext';

export function AdminUsers() {
  const { user: me, refresh } = useAuth();
  const [users, setUsers] = useState<AuthUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api<{ users: AuthUser[] }>('/api/admin/users');
      setUsers(res.users);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onImpersonate = async (id: number) => {
    await api(`/api/admin/users/${id}/impersonate`, { method: 'POST' });
    await refresh();
    window.location.href = '/';
  };

  const onToggleStatus = async (u: AuthUser) => {
    await api(`/api/admin/users/${u.id}`, { method: 'PATCH', body: { status: u.status === 'disabled' ? 'active' : 'disabled' } });
    await load();
  };

  const onDelete = async (u: AuthUser) => {
    if (!confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    await api(`/api/admin/users/${u.id}`, { method: 'DELETE' });
    await load();
  };

  return (
    <div className="scroll-y" style={{ flex: 1, padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Admin</div>
          <h1 style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 600 }}>Users & roles</h1>
          <div className="muted" style={{ fontSize: 12.5, marginTop: 4 }}>
            Who can use this console. People sign in with their Amada login; disabling or deleting someone here ends their access right away.
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          <Icon name="plus" size={12} /> Add user
        </button>
      </div>

      <div className="card">
        <table className="tbl">
          <thead>
            <tr>
              <th>User</th>
              <th>Role</th>
              <th>Status</th>
              <th>Last login</th>
              <th>Created</th>
              <th style={{ width: 320 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 24 }}>Loading…</td></tr>}
            {!loading && users.length === 0 && (
              <tr><td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 24 }}>No users yet.</td></tr>
            )}
            {users.map(u => (
              <tr key={u.id}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div className="avatar" style={{ width: 28, height: 28, fontSize: 11 }}>
                      {(u.name ?? u.email).split(/\s|@/).map(s => s[0]).slice(0, 2).join('').toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 500 }}>{u.name ?? '—'}</div>
                      <div className="muted" style={{ fontSize: 12 }}>{u.email}</div>
                    </div>
                  </div>
                </td>
                <td><span className="badge" style={{ background: u.role === 'admin' ? 'var(--accent-soft)' : 'var(--surface-3)', color: u.role === 'admin' ? 'var(--accent)' : 'var(--text-muted)' }}>{u.role}</span></td>
                <td>
                  {/* 'invited' is a pre-SSO status: the person may sign in and becomes 'active' when they do. */}
                  <span className="pill" title={u.status === 'invited' ? 'Has access; not signed in with their Amada login yet' : undefined}>
                    <span className={`pill-dot dot-${u.status === 'active' ? 'ok' : u.status === 'invited' ? 'info' : 'warn'}`}></span>
                    {u.status === 'invited' ? 'pending' : u.status}
                  </span>
                </td>
                <td className="muted mono" style={{ fontSize: 12 }}>{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : '—'}</td>
                <td className="muted mono" style={{ fontSize: 12 }}>{new Date(u.created_at).toLocaleDateString()}</td>
                <td>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {u.status !== 'disabled' && me && me.id !== u.id && (
                      <button className="btn btn-sm" onClick={() => onImpersonate(u.id)}>Impersonate</button>
                    )}
                    {me && me.id !== u.id && (
                      <button className="btn btn-sm btn-ghost" onClick={() => onToggleStatus(u)}>
                        {u.status === 'disabled' ? 'Enable' : 'Disable'}
                      </button>
                    )}
                    {me && me.id !== u.id && (
                      <button className="btn btn-sm btn-danger" onClick={() => onDelete(u)}>Delete</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateUserModal onClose={() => setShowCreate(false)} onCreated={load} />}
    </div>
  );
}

function CreateUserModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<'admin' | 'reviewer'>('reviewer');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null); setBusy(true);
    try {
      await api('/api/admin/users', { method: 'POST', body: { email, name, role } });
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create user');
    } finally { setBusy(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.36)', zIndex: 90, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div className="card" style={{ width: 'min(440px, 92vw)', padding: 24 }} onClick={e => e.stopPropagation()}>
        <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 600 }}>Add user</h2>
        <div className="muted" style={{ fontSize: 12.5, marginBottom: 16 }}>
          Use the email of their Amada login (the account they use on the other Amada dashboards).
          Nothing is emailed: they just sign in here with their Amada login.
        </div>
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <label className="form-lbl">Email</label>
          <input className="input" type="email" required autoFocus value={email} onChange={e => setEmail(e.target.value)} />
          <label className="form-lbl">Name</label>
          <input className="input" required value={name} onChange={e => setName(e.target.value)} placeholder="Bettina Smith" />
          <label className="form-lbl">Role</label>
          <select className="select" value={role} onChange={e => setRole(e.target.value as 'admin' | 'reviewer')}>
            <option value="reviewer">Reviewer · works the queue, reads dashboard</option>
            <option value="admin">Admin · everything reviewer can + user CRUD + audit triggers + config</option>
          </select>
          {error && <div style={{ padding: '8px 12px', background: 'var(--danger-soft)', border: '1px solid color-mix(in oklch, var(--danger) 30%, var(--border))', borderRadius: 6, color: 'var(--danger)', fontSize: 12.5 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button type="button" className="btn btn-ghost" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={busy}>{busy ? 'Adding…' : 'Add user'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
